<script>
import { mapGetters } from 'vuex'

import Layout from '@layouts/main.vue'

import Translatable from '@src/mixins/translatable.js'
import Resizable from '@src/mixins/resizable.js'
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js'

import { SwiperSlide, Swiper } from 'vue-awesome-swiper'
import 'swiper/css/swiper.css'

import DistrictTabs from '@components/district/district-tabs.vue'
import DistrictMap from '@components/district/district-map.vue'
import Slider from '@components/common/sliders/attractions-slider.vue'
import Card from '@components/prague-attraction-card.vue'
import { trackEvent } from '@/src/utils/analytics'

export default {
  page: {
    title: 'Area',
    meta: [{ name: 'description', content: 'Area page.' }],
  },
  components: {
    DistrictTabs,
    Card,
    Slider,
    Layout,
    SwiperSlide,
    Swiper,
    DistrictMap,
  },
  mixins: [
    Translatable,
    Resizable,
    SanitizerMixin
  ],
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      district: {},
      API_KEY: process.env.GOOGLE_MAPS_API_KEY,
      swiperOptions: {
        autoplay: {
          delay: 5000
        },
        speed: 1300,
        allowTouchMove: true,
        loop: true,
        slidesPerView: 1,
        slidesPerGroup: 1,
        pagination: {
          el: '.header-pagination',
          clickable: true,
          bulletClass: 'bullet',
          bulletActiveClass: 'bullet-active'
        },
      },
      fullMap: false
    }
  },
  computed: {
    ...mapGetters('attractions', [
        'attractions'
    ]),
    ...mapGetters('areas', [
        'areas',
        'nextDistrict',
        'previousDistrict'
    ]),
    pageImg() {
      return this.desktopScreen ? this.district.webimages : this.district.images
    },
    districtId() {
      return this.areas.find(area => area.slug === this.slug)._id
    },
    relatedAttractions() {
      return this.attractions.filter(attraction => attraction.area === this.districtId)
    },
    currentMap() {
      return this.fullMap ? document.querySelector('#map-wrapper-full')
      : document.querySelector('.secondary-column')
    },
  },
  created() {
    // this.$store.dispatch('attractions/fetchAttractions')
    this.unsubscribe = this.$store.subscribe(mutation => {
      if (mutation.type === 'areas/SET_AREAS') {
        this.district = this.areas.find(area => area.slug === this.slug)
      }
    })
    this.$store.dispatch('areas/fetchAreas')
  },
  beforeDestroy() {
    this.unsubscribe()
  },
  methods: {
    getBackground(image) {
      return {
        backgroundImage: `url("${process.env.VUE_APP_STATIC_URL}${image}")`,
        backgroundSize: 'cover'
      }
    },
    openMap() {
      this.fullMap = !this.fullMap
      this.scrollToElement({behavior: 'smooth'})
    },
    scrollToElement(options) {
      if (this.currentMap) {
        this.currentMap.scrollIntoView(options);
      }
    },
    async handleCheckoutClick() {
      if(sessionStorage.getItem('isAdClick') === 'true') {
        await trackEvent('sponsored_click_buy', {
          event_category: 'google_ads',
        });
      }
      await trackEvent('transfer_to_payment', {
        event_category: 'payment',
      });
    },
  }
}
</script>

<template>
  <Layout>
    <section class="header-slider">
      <Swiper v-if="pageImg.length > 1" :options="swiperOptions">
        <SwiperSlide v-for="(image, index) in pageImg" :key="index">
          <div class="slide" :style="getBackground(image)"></div>
        </SwiperSlide>
        <div slot="pagination" class="header-pagination"></div>
      </Swiper>
      <div v-else class="slide" :style="getBackground(pageImg[0])"></div>
    </section>
    <section class="justify-center">
      <div class="container header-content" style="height: 135px">
        <div class="title-block">
          <h1 class="header-title" v-html="getTranslatedContent('title', district.content)"></h1>
          <h3 class="header-subtitle" v-html="getTranslatedContent('subtitle', district.content)"></h3>
        </div>
      </div>
    </section>
    <main class="justify-center">
      <div class="container content">
        <div class="main-column">
          <div class="top-banner">
            <router-link :to="{ name: 'areas' }" class="link">
              <button class="button">{{ getTranslatedContent('AREA_all_areas_btn') }}</button>
            </router-link>
            <p v-html="bannerText(district.content)"></p>
          </div>
        </div>
        <div class="secondary-column">
          <DistrictTabs :district="district"/>
          <div class="order-button" @click="handleCheckoutClick">
            <router-link :to="{ name: 'eshop' }">{{ getTranslatedContent('CALCULATOR_order_your_coolpass') }}</router-link>
          </div>
          <div class="map-wrapper" :class="fullMap && 'disabled'">
            <DistrictMap
              :district="district"
              :fullMap="fullMap"
              @open-map="openMap"
            />
          </div>
        </div>
        <div v-if="desktopScreen" class="description">
          <div class="area-text-section" v-html="description(district.content)">
          </div>
          <div id="map-wrapper-full"></div>
        </div>
      </div>
    </main>
    <section class="justify-center">
      <div v-if="fullMap" class="container map-wrapper-full">
        <DistrictMap
          :district="district"
          :fullMap="fullMap"
          @open-map="openMap"
        />
      </div>
    </section>
    <section class="justify-center">
      <div class="container navigation">
        <router-link
          :to="{
            name: 'district',
            params: {
              slug: previousDistrict(district),
            },
          }"
          class="attraction-nav-btn"
          :class="previousDistrict(district) ? '' : 'disabled'"
        >
          <div class="previous-attraction">
            <i></i>
            <p v-html="getTranslatedContent('AREA_previous')"></p>
          </div>
        </router-link>
        <router-link
          :to="{
            name: 'district',
            params: {
              slug: nextDistrict(district),
            },
          }"
          class="attraction-nav-btn next-attraction"
          :class="nextDistrict(district) ? '' : 'disabled'"
        >
          <div class="next-attraction">
            <p v-html="getTranslatedContent('AREA_next')"></p>
            <i></i>
          </div>
        </router-link>
      </div>
    </section>
    <section>
      <div v-if="!desktopScreen" class="description" v-html="description(district.content)"></div>
    </section>
    <section v-if="relatedAttractions" class="justify-center related-attractions">
      <div class="container">
        <h3>{{ getTranslatedContent('AREA_attractions_in').replace("$name", getTranslatedContent('title', district.content)) }}</h3>
        <div v-if="desktopScreen" class="attractions">
          <Card v-for="(attraction, index) in relatedAttractions" :key="index" :attraction="attraction"/>
        </div>
        <Slider v-else :id="'related'" :attractions="relatedAttractions" :isntPaginated="true"/>
      </div>
    </section>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.header-slider {
  min-height: 550px;
}

.header-content {
  position: absolute;
  top: 217px;
}

.title-block {
  width: 840px;
  height: 135px;
  background: rgba(37, 44, 62, 0.36);
  padding-top: 9px;
  box-sizing: border-box;
}

.header-title {
  font-family: Arial, sans-serif;
  font-weight: bold;
  font-size: 48px;
  line-height: 62px;
  text-transform: uppercase;
  margin-left: 22px;
  color: #FFFFFF;
}

.header-subtitle {
  font-family: Open Sans Semibold, sans-serif;
  font-weight: 600;
  font-size: 22px;
  line-height: 29px;
  color: #FFFFFF;
  margin-left: 25px;
}

::v-deep .header-pagination {
  @extend .pagination;
  position: absolute;
  bottom: 109px;

  & .bullet {
    background: #FFFFFF;
    opacity: 0.8;
  }

  & .bullet-active {
    background: $orange;
    opacity: 1;
  }
}

.bullet {
  background: #FFFFFF;
  opacity: 0.8;
}

.content {
  display: grid;
  grid-template-columns: 751px 367px;
  column-gap: 22px;
  margin-top: -99px;
  z-index: 1;
}

.main-column {
  grid-row: 1 / 1;

  .top-banner {
    box-sizing: border-box;
    padding: 20px 20px 20px 20px;
    min-height: 110px;
    height: auto;
    background: #FFFFFF;
    box-shadow: 0 8px 12px rgba(76, 72, 124, 0.08);
    border-radius: 20px;

    .all-areas-btn {
      width: 81px;
      height: 32px;
      border: 1px solid $dark-blue;
      box-sizing: border-box;
      border-radius: 5px;
      text-transform: capitalize;

      font-family: Open Sans Semibold, sans-serif;
      font-style: normal;
      font-weight: 600;
      font-size: 12px;
      line-height: 32px;
      text-align: center;

      cursor: pointer;
    }

    user-select: none;

    ::v-deep em {
      font-style: italic;
    }

    ::v-deep strong {
      font-family: Open Sans Bold, sans-serif;
      font-style: normal;
      font-weight: bold;
    }

    ::v-deep a {
      color: $gray;
      text-decoration: none;

      &:hover {
        color: $orange;
      }
    }

    p {
      font-family: Open Sans, sans-serif;
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 21px;
      color: $dark-blue;
    }
  }
}

.link {
  text-decoration: none;
  .button {
    min-width: 100px;
    padding: 8px 10px;
    height: 32px;
    border: 1px solid $dark-blue;
    background: $white;
    border-radius: 5px;
    color: $dark-blue;
    font-family: Open Sans Semibold, sans-serif;
    font-style: normal;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 12px;
    line-height: 16px;
    margin: 0 10px 20px 0;

    &:hover {
      border: 1px solid $dark-gray;
    }
  }
}

.secondary-column {
  grid-row: 1 / 4;
}

.order-button {
  width: 367px;
  height: 63px;
  background: $dark-blue;
  border-radius: 0px 0px 10px 10px;

  margin-top: 5px;

  &:hover {
    background: #2F384F;
  }

  & a {
    display: block;
    width: 100%;
    height: 100%;
    line-height: 23px;
    text-align: center;

    text-decoration: none;

    font-family: Open Sans Bold, sans-serif;
    font-weight: bold;
    font-size: 18px;
    text-transform: uppercase;
    color: #FFFFFF;

    padding-top: 20px;
    padding-bottom: 20px;
  }
}

::v-deep .description {
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 23px;
  color: $dark-blue;
  // opacity: 0.8;
  margin-top: 20px;
  user-select: none;

  & em {
    font-style: italic;
  }

  & ul {
    margin-left: 20px;
    list-style: initial;
  }

  & strong {
    font-family: Open Sans Bold, sans-serif;
    font-style: normal;
    font-weight: bold;
  }

  & a {
    color: $gray;
    text-decoration: none;

    &:hover {
      color: $orange;
    }
  }

  & .ql-align-center {
    text-align: center;
  }
}

.related-attractions {
  padding-top: 40px;

  h3 {
    font-family: Open Sans Bold, sans-serif;
    font-style: normal;
    font-weight: bold;
    font-size: 22px;
    line-height: 29px;
    text-transform: uppercase;
    color: $dark-blue;
  }

  .attractions {
    margin-top: 30px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
}

::v-deep .swiper-wrapper,
.swiper-container {
  // we need the slider not to overlap the toggled dropdown menu
  z-index: auto;
}

.slide {
  height: 550px;
  background-repeat: no-repeat;
  background-position: center;
  z-index: -1;
}

.navigation {
  margin-top: 40px;
  display: flex;
  flex-direction: row;

  & .previous-attraction,
  & .next-attraction {
    display: flex;
    flex-direction: row;
    align-items: center;

    cursor: pointer;

    p {
      max-width: 72px;
      font-family: Open Sans, sans-serif;
      font-style: normal;
      font-weight: normal;
      font-size: 12px;
      line-height: 16px;
      text-transform: uppercase;
      color: $dark-blue;
    }

    i {
      display: block;
      width: 24px;
      height: 24px;
    }
  }

  & .previous-attraction {
    p {
      margin-left: 15px;
      text-align: left;
    }

    i {
      background: url("../../../assets/icons/left-arrow-orange.svg") no-repeat center;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  & .next-attraction {
    margin-left: auto;

    p {
      margin-right: 15px;
      text-align: right;
      // width: 30px;
    }

    i {
      background: url("../../../assets/icons/right-arrow-orange.svg") no-repeat center;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  .attraction-nav-btn {
    display: block;
    text-decoration: none;
    user-select: none;
  }
}

.disabled {
  pointer-events: none;
  opacity: 0.2;
}

.map-wrapper, .map-wrapper-full {
  height: 292px;
  margin-top: 30px;

  &.disabled {
    display: none;
  }
}

.map-wrapper-full {
  height: 600px;
}

.map {
  width: 100%;
  margin-top: 30px;
}

@media only screen and (max-width: $screen-width-xs) {
  .header-slider, .slide {
    min-height: 274px;
    height: 244px;
  }

  .header-content {
    top: 70px;
  }

  .header-pagination {
    bottom: 30px;
  }

  .content {
    width: 100%;
    grid-template-columns: 100%;
    margin-top: -25px;
  }

  .secondary-column {
    grid-row: auto;
    .tabs, .order-button {
      width: 100%;
    }
  }

  main, .header-content {
    .container {
      padding: 0;
    }
  }

  .map-wrapper {
    width: 100%;
  }

  .main-column {
    .top-banner {
      width: 100%;
      padding: 20px 8px;
      border-radius: 20px 20px 0 0;

      user-select: none;

      p {
        font-size: 14px;
        line-height: 23px;
        margin-top: 10px;

        ::v-deep a {
          color: $gray;
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }

  .title-block {
    width: 100%;
    min-height: 125px;
    height: auto;
    padding: 8px 13px;

    .header-title {
      font-size: 26px;
      line-height: 34px;
      margin: 10px 8px 10px 0;
    }

    .header-subtitle {
      font-size: 16px;
      line-height: 21px;
      margin: 0;
    }
  }

  .description {
    margin-top: 0px;
    padding: 0 8px;
  }

  .navigation {
    margin-top: 20px;
    margin-bottom: 40px;
  }

  .related-attractions {
    .container {
      padding: 0;
    }

    h3 {
      font-size: 18px;
      line-height: 23px;
      margin-bottom: 15px;
      margin-left: 8px;
    }
  }

  .container.header-content {
    padding: 0;
  }

  .link {
    .button {
      margin: 0 10px 4px 0;
    }
  }
}
</style>
